import { useEffect, useState } from 'react';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { getAuthService } from '@edx/frontend-platform/auth';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Icon,
  Tab,
  Tabs,
} from '@openedx/paragon';
import { ChevronLeft } from '@openedx/paragon/icons';
import PropTypes from 'prop-types';
import { Navigate, useNavigate } from 'react-router-dom';

import BaseContainer from '../base-container';
import { ThirdPartyAuthProvider, useThirdPartyAuthContext } from '../common-components/components/ThirdPartyAuthContext';
import messages from '../common-components/messages';
import { LOGIN_PAGE, REGISTER_PAGE } from '../data/constants';
import {
  getTpaHint, getTpaProvider, updatePathWithQueryParams,
} from '../data/utils';
import { LoginProvider } from '../login/components/LoginContext';
import LoginComponentSlot from '../plugin-slots/LoginComponentSlot';
import { RegistrationPage } from '../register';
import { RegisterProvider } from '../register/components/RegisterContext';

const normalizeChildSafelyLang = (value) => {
  if (!value) {
    return '';
  }

  const lang = String(value).trim().toLowerCase().split(/[-_]/)[0];
  return ['tr', 'en', 'de'].includes(lang) ? lang : '';
};

const readChildSafelyCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }

  const cookies = document.cookie.split(';').map(cookie => cookie.trim());
  const found = cookies.find(cookie => cookie.startsWith(`${name}=`));

  if (!found) {
    return '';
  }

  return decodeURIComponent(found.split('=').slice(1).join('='));
};

const getLangFromSearch = (search) => {
  if (!search) {
    return '';
  }

  const params = new URLSearchParams(search);

  return normalizeChildSafelyLang(
    params.get('lang')
    || params.get('locale')
    || params.get('language')
    || params.get('preview-lang')
    || params.get('lang_refresh'),
  );
};

// CHILDSAFELY_AUTH_LANG_V8
const getChildSafelyLang = (intlLocale) => {
  if (typeof window !== 'undefined') {
    const urlLang = getLangFromSearch(window.location.search);

    if (urlLang) {
      return urlLang;
    }
  }

  const explicitLang = normalizeChildSafelyLang(
    readChildSafelyCookie('childsafely_lang_v3'),
  );

  if (explicitLang) {
    return explicitLang;
  }

  /*
   * Do not trust legacy childsafely_lang here.
   * It may contain an old "en" value while the LMS itself
   * is already Turkish.
   *
   * For a user without a NEW explicit preference,
   * ChildSafely starts in Turkish.
   */
  return 'tr';
};

const getChildSafelyAuthLabels = (intlLocale) => {
  const lang = getChildSafelyLang(intlLocale);

  const labels = {
    tr: {
      register: 'Kayıt Ol',
      signIn: 'Giriş Yap',
    },
    en: {
      register: 'Register',
      signIn: 'Sign in',
    },
    de: {
      register: 'Registrieren',
      signIn: 'Einloggen',
    },
  };

  return labels[lang] || labels.tr;
};

const LogistrationPageInner = ({
  selectedPage,
}) => {
  const tpaHint = getTpaHint();
  const {
    thirdPartyAuthContext,
    clearThirdPartyAuthErrorMessage,
  } = useThirdPartyAuthContext();

  const {
    providers,
    secondaryProviders,
  } = thirdPartyAuthContext;

  const { formatMessage, locale } = useIntl();
  const authLabels = getChildSafelyAuthLabels(locale);

  if (typeof window !== 'undefined') {
    window.__CS_AUTH_LANG__ = getChildSafelyLang(locale);
  }

  const [institutionLogin, setInstitutionLogin] = useState(false);
  const [key, setKey] = useState('');
  const navigate = useNavigate();
  const disablePublicAccountCreation = getConfig().ALLOW_PUBLIC_ACCOUNT_CREATION === false;
  const hideRegistrationLink = getConfig().SHOW_REGISTRATION_LINKS === false;

  useEffect(() => {
    const authService = getAuthService();
    if (authService) {
      authService.getCsrfTokenService()
        .getCsrfToken(getConfig().LMS_BASE_URL);
    }
  }, []);

  useEffect(() => {
    if (disablePublicAccountCreation) {
      navigate(updatePathWithQueryParams(LOGIN_PAGE));
    }
  }, [navigate, disablePublicAccountCreation]);

  const handleInstitutionLogin = (e) => {
    sendTrackEvent('edx.bi.institution_login_form.toggled', { category: 'user-engagement' });
    if (typeof e === 'string') {
      sendPageEvent('login_and_registration', e === '/login' ? 'login' : 'register');
    } else {
      sendPageEvent('login_and_registration', e.target.dataset.eventName);
    }
    setInstitutionLogin(!institutionLogin);
  };

  const handleOnSelect = (tabKey, currentTab) => {
    if (tabKey === currentTab) {
      return;
    }
    sendTrackEvent(`edx.bi.${tabKey.replace('/', '')}_form.toggled`, { category: 'user-engagement' });
    clearThirdPartyAuthErrorMessage();
    setKey(tabKey);
  };

  const tabTitle = (
    <div className="d-flex">
      <Icon src={ChevronLeft} className="left-icon" />
      <span className="ml-2">
        {selectedPage === LOGIN_PAGE
          ? authLabels.signIn
          : authLabels.register}
      </span>
    </div>
  );

  const isValidTpaHint = () => {
    const { provider } = getTpaProvider(tpaHint, providers, secondaryProviders);
    return !!provider;
  };

  return (
    <BaseContainer>
      <div>
        {disablePublicAccountCreation
          ? (
            <>
              {institutionLogin && (
                <Tabs defaultActiveKey="" id="controlled-tab" onSelect={handleInstitutionLogin}>
                  <Tab title={tabTitle} eventKey={LOGIN_PAGE} />
                </Tabs>
              )}
              <div id="main-content" className="main-content">
                {!institutionLogin && (
                  <h3 className="mb-4.5">{authLabels.signIn}</h3>
                )}
                <LoginComponentSlot
                  institutionLogin={institutionLogin}
                  handleInstitutionLogin={handleInstitutionLogin}
                />
              </div>
            </>
          )
          : (
            <div>
              {institutionLogin
                ? (
                  <Tabs defaultActiveKey="" id="controlled-tab" onSelect={handleInstitutionLogin}>
                    <Tab title={tabTitle} eventKey={selectedPage === LOGIN_PAGE ? LOGIN_PAGE : REGISTER_PAGE} />
                  </Tabs>
                )
                : (!isValidTpaHint() && !hideRegistrationLink && (
                  <Tabs
                    defaultActiveKey={selectedPage}
                    id="controlled-tab"
                    onSelect={(tabKey) => handleOnSelect(tabKey, selectedPage)}
                  >
                    <Tab title={authLabels.register} eventKey={REGISTER_PAGE} />
                    <Tab title={authLabels.signIn} eventKey={LOGIN_PAGE} />
                  </Tabs>
                ))}
              {key && (
                <Navigate to={updatePathWithQueryParams(key)} replace />
              )}
              <div id="main-content" className="main-content">
                {!institutionLogin && !isValidTpaHint() && hideRegistrationLink && (
                  <h3 className="mb-4.5">
                    {selectedPage === LOGIN_PAGE ? authLabels.signIn : authLabels.register}
                  </h3>
                )}
                {selectedPage === LOGIN_PAGE
                  ? (
                    <LoginComponentSlot
                      institutionLogin={institutionLogin}
                      handleInstitutionLogin={handleInstitutionLogin}
                    />
                  )
                  : (
                    <RegistrationPage
                      institutionLogin={institutionLogin}
                      handleInstitutionLogin={handleInstitutionLogin}
                    />
                  )}
              </div>
            </div>
          )}
      </div>
    </BaseContainer>
  );
};

LogistrationPageInner.propTypes = {
  selectedPage: PropTypes.string.isRequired,
};

/**
 * Main Logistration Page component wrapped with providers
 */
const LogistrationPage = (props) => (
  <ThirdPartyAuthProvider>
    <RegisterProvider>
      <LoginProvider>
        <LogistrationPageInner {...props} />
      </LoginProvider>
    </RegisterProvider>
  </ThirdPartyAuthProvider>
);

export default LogistrationPage;